import React, { useEffect, useState } from "react";
import NotFound from "@/pages/NotFound";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { fetchPhaseFeatures } from "../../lib/phaseFeatures";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const WEIGHTS = { overdue_rfis: "Overdue RFIs", open_punch: "Open punch items", unacknowledged_logs: "Unacknowledged daily reports", unissued_pay_apps: "Unissued pay applications" };
const PERMS = { maintenance_write: "Log and update maintenance", vendor_assign: "Assign vendors (work orders)", lease_write: "Record leases" };
const MONEY = [["ap_bill_founder_threshold_cents", "Legacy (not used for approvals): AP bill approvals follow the Finance approval policy ($)"],
  ["change_order_founder_threshold_cents", "Change orders at or above this need the Founder ($)"],
  ["contract_founder_threshold_cents", "Contracts at or above this need the Founder ($)"]];
const toForm = (r) => ({ ...r, retention: String(r.retention_bps_default / 100),
  ...Object.fromEntries(MONEY.map(([k]) => [k, String(r[k] / 100)])),
  risk_weights: Object.fromEntries(Object.entries(r.risk_weights).map(([k, v]) => [k, String(v)])) });

function Form({ data, onSaved }) {
  const [f, setF] = useState(toForm(data.rules));
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const save = (e) => {
    e.preventDefault();
    const body = { retention_bps_default: Math.round(Number(f.retention) * 100), client_visibility_default: f.client_visibility_default,
      ...Object.fromEntries(MONEY.map(([k]) => [k, Math.round(Number(f[k]) * 100)])),
      risk_weights: Object.fromEntries(Object.entries(f.risk_weights).map(([k, v]) => [k, Number(v)])), pm_permissions: f.pm_permissions };
    api.put("/admin/founder-rules", body).then(() => { setError(null); setMsg("Saved. The change is recorded in the audit log."); onSaved(); })
      .catch((err) => { setMsg(null); setError(errMsg(err, "Could not save. Check the values.")); });
  };
  const L = "text-sm font-nav text-erf-dark";
  return (
    <form onSubmit={save} className="space-y-6" data-testid="founder-rules-form">
      <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 grid gap-4 sm:grid-cols-2">
        <h2 className="sm:col-span-2 font-display font-semibold text-erf-dark text-base md:text-lg">Money</h2>
        <label className={L}>Default retention for new contracts (%)<Input inputMode="decimal" value={f.retention} onChange={(e) => setF({ ...f, retention: e.target.value })} className="mt-1 h-11" data-testid="founder-rules-retention" /></label>
        {MONEY.map(([k, label]) => (
          <label key={k} className={L}>{label}<Input inputMode="decimal" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-11" data-testid={`founder-rules-${k}`} /></label>
        ))}
      </CardContent></Card>
      <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 grid gap-4 sm:grid-cols-2">
        <h2 className="sm:col-span-2 font-display font-semibold text-erf-dark text-base md:text-lg">Client visibility</h2>
        <label className={L}>Default for new project records
          <select value={f.client_visibility_default} onChange={(e) => setF({ ...f, client_visibility_default: e.target.value })} className="mt-1 block h-11 w-full rounded-md border border-erf-borderCream bg-white px-3" data-testid="founder-rules-visibility">
            <option value="none">None shared (safest)</option>
            <option value="summary_only">Summary only</option>
            <option value="explicit_mark">Only items I mark as shared</option>
          </select>
        </label>
        <p className="text-xs font-nav text-erf-dark/80 self-end">Clients still only see items you share from Client Sharing.</p>
      </CardContent></Card>
      <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <h2 className="sm:col-span-2 lg:col-span-4 font-display font-semibold text-erf-dark text-base md:text-lg">Construction IQ risk weights (0–10)</h2>
        {Object.entries(WEIGHTS).map(([k, label]) => (
          <label key={k} className={L}>{label}<Input inputMode="decimal" value={f.risk_weights[k]} onChange={(e) => setF({ ...f, risk_weights: { ...f.risk_weights, [k]: e.target.value } })} className="mt-1 h-11" data-testid={`founder-rules-weight-${k}`} /></label>
        ))}
      </CardContent></Card>
      <Card className="ds-card border-erf-borderCream"><CardContent className="p-5 space-y-2">
        <h2 className="font-display font-semibold text-erf-dark text-base md:text-lg">Property Manager permissions</h2>
        {Object.entries(PERMS).map(([k, label]) => (
          <label key={k} className={`${L} flex min-h-[44px] items-center gap-3`}>
            <input type="checkbox" className="h-5 w-5" checked={f.pm_permissions[k]} onChange={(e) => setF({ ...f, pm_permissions: { ...f.pm_permissions, [k]: e.target.checked } })} data-testid={`founder-rules-pm-${k}`} />{label}
          </label>
        ))}
        <p className="text-xs font-nav text-erf-dark/80">Property Managers can never see financial or architect records, and can never export tenant data.</p>
      </CardContent></Card>
      <Button type="submit" className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="founder-rules-save">Save rules</Button>
      {msg && <p role="status" className="text-sm font-nav text-erf-dark" data-testid="founder-rules-saved">{msg}</p>}
      {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="founder-rules-error">{error}</p>}
    </form>
  );
}

export default function FounderRules() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [anyOn, setAnyOn] = useState(null);
  const load = () => api.get("/admin/founder-rules").then((r) => setData(r.data));
  useEffect(() => {
    if (!user?.id) return;
    fetchPhaseFeatures(user.id).then((d) => setAnyOn((d.phases || []).some((p) => p.phase >= 2 && p.phase <= 8 && p.enabled)));
  }, [user?.id]);
  useEffect(() => { if (anyOn && user?.role_key === "OWNER_SUPER_ADMIN") load(); }, [anyOn, user?.role_key]);
  if (anyOn === false || (user && user.role_key !== "OWNER_SUPER_ADMIN")) return <NotFound />;
  if (anyOn === null) return null;
  return (
    <AdminAppShell activeNav="founderrules" user={user} title="Founder business rules" subtitle={data?.customized ? "Your saved rules apply to this company." : "Safe defaults apply until you save."}
      breadcrumbs={[{ label: "Owner Command Center", to: "/owner" }, { label: "Founder rules", to: null }]}>
      <div data-testid="founder-rules-page">
        {data && <Form key={data.updated_at || "default"} data={data} onSaved={load} />}
      </div>
    </AdminAppShell>
  );
}
